<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <h6 class="text-h6 font-weight-bold">
                  페이지 편집
                </h6>
                <p class="text-body-2">
                  그룹을 선택하고, 논문 순서를 변경하거나 개별 논문의 내용을 편집할 수 있습니다.
                </p>
              </v-col>
            </v-row>

            <v-row>
              <!-- 좌측: 그룹 선택 + 논문 목록 (순서 변경) -->
              <v-col cols="12" xl="4" lg="4" md="5" sm="12">
                <v-card class="rounded-lg overflow-y-auto" style="height: calc(100vh - 200px); min-height: 600px;" outlined>
                  <v-card-text>
                    <!-- 그룹 선택 -->
                    <div class="mb-4">
                      <p class="text-caption grey--text mb-2 font-weight-bold">그룹 선택</p>
                      <v-select
                        v-model="selectedGroupIdx"
                        :items="groups"
                        :item-text="(item) => item.title"
                        :item-value="(item) => item.idx"
                        label="그룹(장) 선택"
                        outlined
                        dense
                        hide-details
                        @change="handleGroupChange"
                      ></v-select>
                    </div>

                    <v-divider class="mb-4"></v-divider>

                    <!-- 순서 변경 모드 토글 -->
                    <div class="d-flex justify-space-between align-center mb-3">
                      <p class="text-caption grey--text mb-0 font-weight-bold">논문 목록</p>
                      <v-btn
                        v-if="!isReordering"
                        color="grey darken-2"
                        x-small
                        outlined
                        @click="isReordering = true"
                      >
                        <v-icon size="14" left>mdi-swap-vertical</v-icon>
                        순서 변경
                      </v-btn>
                      <div v-else>
                        <v-btn
                          color="success"
                          x-small
                          class="mr-1"
                          @click="saveOrder"
                        >
                          <v-icon size="14" left>mdi-check</v-icon>
                          저장
                        </v-btn>
                        <v-btn
                          color="grey"
                          x-small
                          outlined
                          @click="cancelReorder"
                        >
                          취소
                        </v-btn>
                      </div>
                    </div>

                    <!-- 논문 목록 (드래그 앤 드롭) -->
                    <div v-if="papers.length === 0" class="text-center pa-4">
                      <v-icon size="48" color="grey lighten-1">mdi-file-document-outline</v-icon>
                      <p class="text-body-2 grey--text mt-2">
                        그룹을 선택하거나, 선택된 그룹에 논문이 없습니다.
                      </p>
                    </div>

                    <draggable
                      v-else
                      v-model="papers"
                      :disabled="!isReordering"
                      @end="onDragEnd"
                      item-key="idx"
                    >
                      <div v-for="(paper, index) in papers" :key="paper.idx">
                        <v-card
                          flat
                          outlined
                          class="rounded-lg mb-2"
                          :class="{
                            'primary lighten-5': selectedPaper?.idx === paper.idx,
                            'drag-item': isReordering
                          }"
                        >
                          <v-card-text class="pa-3">
                            <div class="d-flex align-center">
                              <!-- 드래그 핸들 -->
                              <v-icon
                                v-if="isReordering"
                                class="drag-handle mr-2"
                                color="grey"
                              >
                                mdi-drag
                              </v-icon>

                              <!-- 순번 -->
                              <v-sheet
                                :color="selectedPaper?.idx === paper.idx ? 'primary' : 'grey darken-3'"
                                class="rounded-lg d-flex align-center justify-center mr-3"
                                width="32"
                                height="32"
                              >
                                <span class="white--text text-body-2 font-weight-bold">
                                  {{ index + 1 }}
                                </span>
                              </v-sheet>

                              <!-- 논문 정보 -->
                              <div class="flex-grow-1" style="min-width: 0;">
                                <p class="text-body-2 font-weight-medium mb-0 text-truncate">
                                  {{ paper.title }}
                                </p>
                                <p v-if="paper.title_eng" class="text-caption grey--text mb-0 text-truncate">
                                  {{ paper.title_eng }}
                                </p>
                              </div>

                              <!-- 편집 버튼 -->
                              <div v-if="!isReordering" class="d-flex align-center">
                                <v-btn
                                  color="primary"
                                  x-small
                                  class="mr-1"
                                  @click="openEditModal(paper)"
                                  title="WYSIWYG 편집"
                                >
                                  <v-icon size="14">mdi-pencil</v-icon>
                                  <span class="ml-1" style="font-size: 9px;">v1</span>
                                </v-btn>
                                <v-btn
                                  color="secondary"
                                  x-small
                                  class="mr-1"
                                  @click="openEditModalV2(paper)"
                                  title="텍스트/WYSIWYG/HTML 편집"
                                >
                                  <v-icon size="14">mdi-pencil</v-icon>
                                  <span class="ml-1" style="font-size: 9px;">v2</span>
                                </v-btn>
                                <v-btn
                                  color="grey"
                                  x-small
                                  outlined
                                  @click="showPreview(paper)"
                                >
                                  <v-icon size="14">mdi-eye</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>
                      </div>
                    </draggable>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- 우측: 미리보기 영역 -->
              <v-col cols="12" xl="8" lg="8" md="7" sm="12">
                <v-card class="rounded-lg" style="height: calc(100vh - 200px); min-height: 600px;" outlined>
                  <!-- 미리보기 미선택 -->
                  <v-card-text v-if="!selectedPaper" class="fill-height">
                    <div class="d-flex flex-column justify-center align-center fill-height">
                      <v-icon size="64" color="grey lighten-1">mdi-eye-outline</v-icon>
                      <p class="text-body-1 grey--text mt-4">
                        논문을 선택하면 미리보기가 표시됩니다
                      </p>
                      <p class="text-body-2 grey--text">
                        목록에서 <v-icon size="16" color="grey">mdi-eye</v-icon> 버튼을 클릭하세요
                      </p>
                    </div>
                  </v-card-text>

                  <!-- 미리보기 표시 -->
                  <v-card-text v-else class="overflow-y-auto grey lighten-4" style="height: calc(100vh - 240px); min-height: 560px;">
                    <div class="d-flex justify-space-between align-center mb-4">
                      <h6 class="text-subtitle-1 font-weight-bold">
                        {{ selectedPaper.title }}
                      </h6>
                      <v-btn text small color="grey" @click="selectedPaper = null">
                        <v-icon size="16" left>mdi-close</v-icon>
                        닫기
                      </v-btn>
                    </div>
                    <div class="d-flex justify-center">
                      <div ref="previewContent">
                        <component
                          :is="selectedPdfTemplate"
                          :item="selectedPaper"
                          :journal="journal"
                          :startPageNumber="1"
                        />
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 편집 모달 -->
    <v-dialog
      v-model="showEditModal"
      max-width="1400px"
      scrollable
      persistent
      :transition="false"
      :content-class="isFullscreenV1 ? 'fullscreen-dialog' : ''"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">논문 내용 편집</span>
          <div class="d-flex align-center">
            <v-btn icon @click="toggleFullscreenV1" class="mr-2">
              <v-icon>{{ isFullscreenV1 ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
            <v-btn icon @click="closeEditModal">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4" :style="{ height: isFullscreenV1 ? 'calc(100vh - 120px)' : 'calc(100vh - 200px)', overflow: 'hidden' }">
          <v-row v-if="editingPaper" class="fill-height" style="margin: 0;">
            <!-- 좌측: 에디터 -->
            <v-col cols="12" lg="8" md="7" class="overflow-y-auto" :style="{ height: '100%', paddingRight: '12px' }">
              <!-- 논문 기본 정보 -->
              <v-card outlined class="rounded-lg mb-4">
                <v-card-text class="pa-3">
                  <p class="text-subtitle-2 font-weight-bold mb-2">논문 정보</p>
                  <v-row dense>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editingPaper.title"
                        label="제목"
                        dense
                        hide-details="auto"
                        class="mb-2"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editingPaper.title_eng"
                        label="제목 (영문)"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- 본문 에디터 -->
              <div class="mb-4">
                <p class="text-subtitle-2 font-weight-bold mb-2">
                  {{ journal.journal_type?.code === 'abst' ? '초록' : '본문' }}
                </p>
                <submission-editor-card
                  v-if="journal.journal_type?.code === 'abst'"
                  :key="`edit-abstract-${editingPaper.idx}`"
                  :value="editingPaper.abstract"
                  @update-content="editingPaper.abstract = $event"
                  :height="isFullscreenV1 ? 2000 : 700"
                  :comments="editingPaper.comments"
                  @update:comments="editingPaper.comments = $event"
                ></submission-editor-card>
                <submission-editor-card
                  v-else
                  :key="`edit-content-${editingPaper.idx}`"
                  :value="editingPaper.content"
                  @update-content="editingPaper.content = $event"
                  :height="isFullscreenV1 ? 2000 : 700"
                  :comments="editingPaper.comments"
                  @update:comments="editingPaper.comments = $event"
                ></submission-editor-card>
              </div>
            </v-col>

            <!-- 우측: 미리보기 + 주석 목록 -->
            <v-col cols="12" lg="4" md="5" class="overflow-y-auto" :style="{ height: '100%' }">
              <v-card outlined class="rounded-lg grey lighten-4">
                <v-card-text>
                  <p class="text-subtitle-2 font-weight-bold mb-3">미리보기</p>
                  <div class="d-flex justify-center" style="transform: scale(0.6); transform-origin: top center;">
                    <component
                      :is="selectedPdfTemplate"
                      :item="editingPaper"
                      :journal="journal"
                      :startPageNumber="1"
                    />
                  </div>
                </v-card-text>
              </v-card>

            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeEditModal">
            취소
          </v-btn>
          <v-btn color="success" @click="saveEditingPaper" :loading="isLoading">
            <v-icon left size="18">mdi-content-save</v-icon>
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 편집 모달 V2 (Text/WYSIWYG/HTML 모드) -->
    <v-dialog
      v-model="showEditModalV2"
      max-width="1400px"
      scrollable
      persistent
      :transition="false"
      :content-class="isFullscreenV2 ? 'fullscreen-dialog' : ''"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">논문 내용 편집 (v2)</span>
          <div class="d-flex align-center">
            <v-btn icon @click="toggleFullscreenV2" class="mr-2">
              <v-icon>{{ isFullscreenV2 ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
            <v-btn icon @click="closeEditModalV2">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4" :style="{ height: isFullscreenV2 ? 'calc(100vh - 120px)' : 'calc(100vh - 200px)', overflow: 'hidden' }">
          <v-row v-if="editingPaperV2" class="fill-height" style="margin: 0;">
            <!-- 좌측: 에디터 -->
            <v-col cols="12" lg="8" md="7" class="overflow-y-auto" :style="{ height: '100%', paddingRight: '12px' }">
              <!-- 논문 기본 정보 -->
              <v-card outlined class="rounded-lg mb-4">
                <v-card-text class="pa-3">
                  <p class="text-subtitle-2 font-weight-bold mb-2">논문 정보</p>
                  <v-row dense>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editingPaperV2.title"
                        label="제목"
                        dense
                        hide-details="auto"
                        class="mb-2"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editingPaperV2.title_eng"
                        label="제목 (영문)"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- 본문 에디터 -->
              <div class="mb-4">
                <p class="text-subtitle-2 font-weight-bold mb-2">
                  {{ journal.journal_type?.code === 'abst' ? '초록' : '본문' }}
                </p>
                <submission-editor-card-legacy
                  v-if="journal.journal_type?.code === 'abst'"
                  :key="`edit-abstract-v2-${editingPaperV2.idx}`"
                  :value="editingPaperV2.abstract"
                  @update-content="editingPaperV2.abstract = $event"
                  :height="isFullscreenV2 ? 2000 : 700"
                  :comments="editingPaperV2.comments"
                  @add-comment="addComment"
                  hide-footnote-list
                ></submission-editor-card-legacy>
                <submission-editor-card-legacy
                  v-else
                  :key="`edit-content-v2-${editingPaperV2.idx}`"
                  :value="editingPaperV2.content"
                  @update-content="editingPaperV2.content = $event"
                  :height="isFullscreenV2 ? 2000 : 700"
                  :comments="editingPaperV2.comments"
                  @add-comment="addComment"
                  hide-footnote-list
                ></submission-editor-card-legacy>
              </div>
            </v-col>

            <!-- 우측: 미리보기 + 주석 목록 -->
            <v-col cols="12" lg="4" md="5" class="overflow-y-auto" :style="{ height: '100%' }">
              <v-card outlined class="rounded-lg grey lighten-4">
                <v-card-text>
                  <p class="text-subtitle-2 font-weight-bold mb-3">미리보기</p>
                  <div class="d-flex justify-center" style="transform: scale(0.6); transform-origin: top center;">
                    <component
                      :is="selectedPdfTemplate"
                      :item="editingPaperV2"
                      :journal="journal"
                      :startPageNumber="1"
                    />
                  </div>
                </v-card-text>
              </v-card>

              <!-- 주석 목록 (미리보기 아래) -->
              <v-card outlined class="rounded-lg mt-3">
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <p class="text-subtitle-2 font-weight-bold mb-0">주석 목록</p>
                    <v-chip v-if="editingPaperV2.comments && editingPaperV2.comments.length > 0"
                      x-small class="ml-2" color="primary" text-color="white">
                      {{ editingPaperV2.comments.length }}개
                    </v-chip>
                  </div>
                  <div v-if="!editingPaperV2.comments || editingPaperV2.comments.length === 0"
                    class="text-center grey--text text-body-2 py-4">
                    등록된 주석이 없습니다.
                  </div>
                  <div v-else class="footnote-list-v2">
                    <div v-for="(comment, index) in editingPaperV2.comments" :key="index" class="footnote-item-v2">
                      <div class="d-flex align-start">
                        <sup class="primary--text font-weight-bold mr-1">{{ comment.superscript || (index + 1) + ')' }}</sup>
                        <span class="text-body-2">{{ comment.content }}</span>
                      </div>
                      <v-btn icon x-small color="error" @click="removeComment(index)" class="ml-2">
                        <v-icon x-small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeEditModalV2">
            취소
          </v-btn>
          <v-btn color="success" @click="saveEditingPaperV2" :loading="isLoading">
            <v-icon left size="18">mdi-content-save</v-icon>
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
/* eslint-disable no-console */
import draggable from "vuedraggable";
import SubmissionEditorCard from "@/components/common/Cards/SubmissionEditorCard.vue";
import SubmissionEditorCardLegacy from "@/components/common/Cards/SubmissionEditorCardLegacy.vue";

import ArticlePaperPage from "@/components/Templates/article/ArticlePaperPage.vue";
import AbstractPaperPage from "@/components/Templates/abstract/AbstractPaperPage.vue";
import ProceedingsPaperPage from "@/components/Templates/proceedings/ProceedingsPaperPage.vue";

import { journalContentGroupApi, journalContentApi, journalApi } from "@/api/api";

export default {
  name: "edit-paper-pages",
  components: {
    draggable,
    SubmissionEditorCard,
    SubmissionEditorCardLegacy,
    ArticlePaperPage,
    AbstractPaperPage,
    ProceedingsPaperPage,
  },
  props: {
    journal_idx: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      journal: {},
      groups: [],
      selectedGroupIdx: null,
      papers: [],
      originalPapers: [],
      selectedPaper: null,
      isReordering: false,
      showEditModal: false,
      editingPaper: null,
      showEditModalV2: false,
      editingPaperV2: null,
      isFullscreenV1: false,
      isFullscreenV2: false,
    };
  },
  computed: {
    selectedPdfTemplate() {
      if (this.journal?.journal_type?.code === "abst") {
        return "abstract-paper-page";
      } else if (this.journal?.journal_type?.code === "procs") {
        return "proceedings-paper-page";
      }
      return "article-paper-page";
    },
  },
  methods: {
    /****************************************************************************************
     * 그룹 관련
     ****************************************************************************************/
    async handleGroupChange() {
      if (this.selectedGroupIdx) {
        await this.fetchPapersByGroup();
        this.originalPapers = JSON.parse(JSON.stringify(this.papers));
      } else {
        this.papers = [];
      }
      this.selectedPaper = null;
    },

    /****************************************************************************************
     * 순서 변경
     ****************************************************************************************/
    onDragEnd() {
      this.papers.forEach((paper, index) => {
        paper.order_number = index + 1;
      });
    },

    async saveOrder() {
      try {
        this.isLoading = true;
        const data = this.papers.map((paper, index) => ({
          idx: paper.idx,
          order_number: index + 1,
        }));
        const response = await journalContentApi.updateJournalContentOrder(data);
        if (response.status === 200) {
          this.notifyVue({
            message: "순서가 저장되었습니다.",
            style: "success",
          });
          this.isReordering = false;
          this.originalPapers = JSON.parse(JSON.stringify(this.papers));
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "순서 저장에 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    cancelReorder() {
      this.papers = JSON.parse(JSON.stringify(this.originalPapers));
      this.isReordering = false;
    },

    /****************************************************************************************
     * 편집 모달
     ****************************************************************************************/
    async openEditModal(paper) {
      try {
        this.isLoading = true;
        const response = await journalContentApi.showJournalContent({ idx: paper.idx });
        if (response.status === 200) {
          this.editingPaper = response.data.response;
          this.showEditModal = true;
          // 미리보기도 동일한 데이터로 업데이트
          if (this.selectedPaper?.idx === paper.idx) {
            this.selectedPaper = { ...this.editingPaper };
          }
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "논문 정보를 불러오는데 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingPaper = null;
      this.isFullscreenV1 = false;
    },

    toggleFullscreenV1() {
      this.isFullscreenV1 = !this.isFullscreenV1;
    },

    async saveEditingPaper() {
      try {
        this.isLoading = true;
        const response = await journalContentApi.updateJournalContent(this.editingPaper);
        if (response.status === 200) {
          this.notifyVue({
            message: "저장되었습니다.",
            style: "success",
          });
          // 목록 새로고침
          await this.fetchPapersByGroup();
          // 미리보기 업데이트
          if (this.selectedPaper?.idx === this.editingPaper.idx) {
            this.selectedPaper = { ...this.editingPaper };
          }
          this.closeEditModal();
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "저장에 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    /****************************************************************************************
     * 편집 모달 V2
     ****************************************************************************************/
    async openEditModalV2(paper) {
      try {
        this.isLoading = true;
        const response = await journalContentApi.showJournalContent({ idx: paper.idx });
        if (response.status === 200) {
          this.editingPaperV2 = response.data.response;
          this.showEditModalV2 = true;
          // 미리보기도 동일한 데이터로 업데이트
          if (this.selectedPaper?.idx === paper.idx) {
            this.selectedPaper = { ...this.editingPaperV2 };
          }
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "논문 정보를 불러오는데 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    closeEditModalV2() {
      this.showEditModalV2 = false;
      this.editingPaperV2 = null;
      this.isFullscreenV2 = false;
    },

    toggleFullscreenV2() {
      this.isFullscreenV2 = !this.isFullscreenV2;
    },

    async saveEditingPaperV2() {
      try {
        this.isLoading = true;
        const response = await journalContentApi.updateJournalContent(this.editingPaperV2);
        if (response.status === 200) {
          this.notifyVue({
            message: "저장되었습니다.",
            style: "success",
          });
          // 목록 새로고침
          await this.fetchPapersByGroup();
          // 미리보기 업데이트
          if (this.selectedPaper?.idx === this.editingPaperV2.idx) {
            this.selectedPaper = { ...this.editingPaperV2 };
          }
          this.closeEditModalV2();
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "저장에 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    /****************************************************************************************
     * 주석 관리 (V2 전용)
     ****************************************************************************************/
    addComment(comment) {
      if (!this.editingPaperV2.comments) {
        this.editingPaperV2.comments = [];
      }
      this.editingPaperV2.comments.push(comment);
    },

    removeComment(index) {
      if (this.editingPaperV2.comments && this.editingPaperV2.comments.length > index) {
        this.editingPaperV2.comments.splice(index, 1);
      }
    },

    /****************************************************************************************
     * 미리보기
     ****************************************************************************************/
    async showPreview(paper) {
      try {
        this.isLoading = true;
        const response = await journalContentApi.showJournalContent({ idx: paper.idx });
        if (response.status === 200) {
          this.selectedPaper = response.data.response;
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "논문 정보를 불러오는데 실패했습니다.",
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },

    /****************************************************************************************
     * API
     ****************************************************************************************/
    async fetchJournal() {
      try {
        const params = { idx: this.journal_idx };
        const response = await journalApi.showJournal(params);
        if (response.status === 200) {
          this.journal = response.data.response;
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "저널 정보를 불러오는데 실패했습니다.",
          style: "danger",
        });
      }
    },

    async fetchGroups() {
      try {
        const params = { journal_idx: this.journal_idx };
        const response = await journalContentGroupApi.showAll(params);
        if (response.status === 200) {
          this.groups = response.data.response;
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "그룹 목록을 불러오는데 실패했습니다.",
          style: "danger",
        });
      }
    },

    async fetchPapersByGroup() {
      try {
        const params = { group_idx: this.selectedGroupIdx };
        const response = await journalContentApi.showContentsByGroup(params);
        if (response.status === 200) {
          const allPapers = response.data.response || [];
          // 공페이지 필터링 (페이지 수 조정용 빈 페이지는 논문이 아님)
          this.papers = allPapers.filter(paper =>
            paper.category !== "empty" &&
            paper.title !== "공페이지" &&
            paper.title !== "빈페이지"
          );
        }
      } catch (error) {
        console.error(error);
        this.notifyVue({
          message: "논문 목록을 불러오는데 실패했습니다.",
          style: "danger",
        });
      }
    },

    /****************************************************************************************
     * 알럿
     ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
      timeout = 5000,
    } = {}) {
      this.$notify({
        timeout: timeout,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },
  },
  watch: {
    // V1 에디터에서 내용 변경 시 메인 미리보기도 동기화
    "editingPaper.content": {
      handler(newContent) {
        if (this.editingPaper && this.selectedPaper?.idx === this.editingPaper.idx) {
          this.selectedPaper = { ...this.selectedPaper, content: newContent };
        }
      },
    },
    "editingPaper.abstract": {
      handler(newAbstract) {
        if (this.editingPaper && this.selectedPaper?.idx === this.editingPaper.idx) {
          this.selectedPaper = { ...this.selectedPaper, abstract: newAbstract };
        }
      },
    },
    "editingPaper.comments": {
      handler(newComments) {
        if (this.editingPaper && this.selectedPaper?.idx === this.editingPaper.idx) {
          this.selectedPaper = { ...this.selectedPaper, comments: newComments };
        }
      },
      deep: true,
    },
    // V2 에디터에서 내용 변경 시 메인 미리보기도 동기화
    "editingPaperV2.content": {
      handler(newContent) {
        if (this.editingPaperV2 && this.selectedPaper?.idx === this.editingPaperV2.idx) {
          this.selectedPaper = { ...this.selectedPaper, content: newContent };
        }
      },
    },
    "editingPaperV2.abstract": {
      handler(newAbstract) {
        if (this.editingPaperV2 && this.selectedPaper?.idx === this.editingPaperV2.idx) {
          this.selectedPaper = { ...this.selectedPaper, abstract: newAbstract };
        }
      },
    },
    "editingPaperV2.comments": {
      handler(newComments) {
        if (this.editingPaperV2 && this.selectedPaper?.idx === this.editingPaperV2.idx) {
          this.selectedPaper = { ...this.selectedPaper, comments: newComments };
        }
      },
      deep: true,
    },
  },
  async mounted() {
    await this.fetchJournal();
    await this.fetchGroups();
  },
};
</script>

<style scoped>
.fill-height {
  height: 100%;
}

.drag-item {
  cursor: grab;
}

.drag-item:active {
  cursor: grabbing;
}

.drag-handle {
  cursor: grab;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.footnote-list-v2 {
  max-height: 200px;
  overflow-y: auto;
}

.footnote-item-v2 {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px dashed #e0e0e0;
}

.footnote-item-v2:last-child {
  border-bottom: none;
}
</style>

<style>
/* 전체보기 모달 - Vuetify v-dialog max-height 90% 제한 해제 */
.v-dialog.fullscreen-dialog {
  max-height: 100vh !important;
  height: 100vh !important;
  margin: 0 !important;
}
</style>
